<template>
  <NuxtLayout>
    <div class="registration container">
      <h1 v-text="page.title" />

      <div class="customError__Wrapper">
        <div class="customError" v-if="userAlreadyExists">
          <p v-text="$t('formErrors.userExists')" />
        </div>
        <div v-if="customError && Object.keys(customError).length" class="customError">
          <p v-text="$t(`loyaltyErrors.error${ customError.code }`)" />
        </div>
      </div>

      <div class="registration__FormWrapper">
        <h2 v-text="$t('registration.subtitle')" />

        <form @submit.prevent="submitRegistration" class="absoluteLabelsForm registrationForm">
          <FieldGroup>
            <TextField v-model="form.email" :label="$t('forms.email')" type="email" :errors="formErrors?.field_errors?.email" />
            <TextField class="form__PasswordField form__PasswordFirstField" v-model="form.password" :label="$t('forms.password')" type="password" :info="$t('forms.passwordNotice')" :errors="formErrors?.field_errors?.password">
              <template #append>
                <button type="button" @click="togglePasswordVisibility">
                  <Icon name="password" />
                  <Icon name="password-hide" />
                </button>
              </template>
            </TextField>
            <TextField class="form__PasswordField form__PasswordRepeatField" v-model="form.confirm_password" :label="$t('forms.passwordRepeat')" type="password" :errors="formErrors?.field_errors?.confirm_password">
              <template #append>
                <button type="button" @click="togglePasswordVisibility($event, repeat = true)">
                  <Icon name="password" />
                  <Icon name="password-hide" />
                </button>
              </template>
            </TextField>
          </FieldGroup>

          <FieldGroup :inline="true">
            <TextField v-model="form.first_name" :label="$t('forms.firstName')" :errors="formErrors?.field_errors?.first_name" />
            <TextField v-model="form.last_name" :label="$t('forms.lastName')" :errors="formErrors?.field_errors?.last_name" />
          </FieldGroup>
          <TextField v-model="form.birth_date" type="date" :label="$t('forms.birthDate')" :errors="formErrors?.field_errors?.birth_date" />
          <SelectField class="form__HalfWidthField" v-model="form.gender" :options="genderOptions" :label="$t('forms.gender')" :errors="formErrors?.field_errors?.gender" />
           
          <FieldGroup>
            <PhoneNumberField
              v-model:numberValue="form.phone_number"
              v-model:codeValue="form.dial_code"
              :label="$t('forms.mobile')"
            />
            <SelectField v-model="form.country_code" :options="countriesList" :label="$t('forms.country')" text-key="name" value-key="code" autocomplete :errors="formErrors?.field_errors?.country_code" />
            <TextField v-model="form.postal_code" :label="$t('forms.postalCode')" :errors="formErrors?.field_errors?.postal_code" />
            <TextField v-model="form.city" :label="$t('forms.city')" :errors="formErrors?.field_errors?.city" />
            <TextField v-model="form.address" :label="$t('forms.streetAddress')" :errors="formErrors?.field_errors?.address" />
            <SelectField v-model="form.preferred_language" :options="communicationOptions" :label="$t('forms.preferredLanguage')" :errors="formErrors?.field_errors?.preferred_language" />
          </FieldGroup>

          <FieldGroup>
            <CheckboxField v-model="form.terms" :errors="formErrors?.field_errors?.terms">
              <i18n-t tag="p" scope="global" keypath="registration.checkbox">
								<template #link>
									<nuxt-link :to="link('LoyaltyTerms')" target="_blank">{{ $t('registration.link') }}</nuxt-link>
								</template>
							</i18n-t>
            </CheckboxField>
            <CheckboxField v-model="form.auto_login" :errors="formErrors?.field_errors?.auto_login">
              <p>{{ $t('registration.autoLogin') }}</p>
            </CheckboxField>
          </FieldGroup>

          <Button type="button" class="registration__FormSubmitButton" medium full-width :disabled="requestInProgress || !form.terms">{{ $t('registration.register') }}</Button>
        </form>      
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup>
  import countriesList from '~/data/countries';
  import { communicationOptions } from '~/data/formsInitialData';

  defineOptions({
    auth: 'aminess'
  });

  const props = defineProps({
    page: {
      type: Object
    }
  })

  const { t } = useI18n();
  const { link } = useSetup();
  const { login } = useAuth('aminess');
  
  const formErrors = ref(null);
  const requestInProgress = ref(false);
  const { customError, setCustomError } = useCustomFormErrors(formErrors.value);
  const userAlreadyExists = ref(false);

  const genderOptions = [
    { title: t('genders.female'), value: 'Female' },
    { title: t('genders.male'), value: 'Male' },
  ];

  const form = ref({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    birth_date: '0000-00-00',
    gender: '',
    phone_number: '',
    dial_code: '+385',
    country_code: '',
    postal_code: '',
    city: '',
    address: '',
    preferred_language: '',
    terms: false,
    auto_login: false,
    //type: 'standalone',
  })

  async function submitRegistration() {
    customError.value = null;
    userAlreadyExists.value = false;
    requestInProgress.value = true;
    let body = JSON.parse(JSON.stringify(form.value));
    for (let [key, value] of Object.entries(body)) {
      if (value === '') {
        body[key] = null;
      }
      else if (key === 'birth_date' && value === '0000-00-00') {
        body[key] = null;
      }
      if (key === 'phone_number') {
        body[key] = value.toString();
      }
    }
    try {
      await useAuthFetch(`loyalty/register/`, {
        method: 'POST',
        strategy: 'aminess',
        body: body,
        headers: {
          'Application-Origin': 'SHAPE-WEB'
        }
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'register',
        'method': 'email',
      });
      if (form.value.auto_login) {
        await login({ email: form.value.email, password: form.value.password }, {
          headers: {
            'Application-Origin': 'SHAPE-WEB'
          }
        });
      }
      await navigateTo(link('RegistrationCompleted'));
    } catch(error) {
      if (error.response?._data) formErrors.value = error.response._data;

      if (formErrors.value?.error_code === 107) {
        formErrors.value.field_errors = {
          password: 107,
          confirm_password: 107,
        }
      }

      if (error.response?._data?.errors && error.response?._data?.errors['profile.email_already_exists']) {
        /*formErrors.value.field_errors = {
          email: 'user_exists',
        };*/
        userAlreadyExists.value = true;
      }

      if (error.response?._data?.errors && error.response?._data?.errors['loyalty.passwords_do_not_match']) {
        formErrors.value.field_errors = {
          password: 107,
          confirm_password: 107,
        };
      }
      
      setCustomError(formErrors.value);
    }
    requestInProgress.value = false;
  }
</script>

<style lang="scss">
  @import url('@/assets/styles/pages/_registration.scss');
</style>